// CrewMarketPanel.jsx - Browse and hire crew with RPG-style stats

import React, { useState } from 'react';

export function CrewMarketPanel({ crewState, cash, onHire, onFire, onClose }) {
  const [viewMode, setViewMode] = useState('market'); // market | roster
  const [sortBy, setSortBy] = useState('salary'); // salary | experience | reliability
  const [roleFilter, setRoleFilter] = useState('all'); // all | captain | first_officer | flight_attendant

  const roster = crewState?.roster || [];
  const market = crewState?.market || [];

  // Count by role
  const marketCounts = {
    captain: market.filter(c => c.role === 'captain').length,
    first_officer: market.filter(c => c.role === 'first_officer').length,
    flight_attendant: market.filter(c => c.role === 'flight_attendant').length,
  };

  const rosterCounts = {
    captain: roster.filter(c => c.role === 'captain').length,
    first_officer: roster.filter(c => c.role === 'first_officer').length,
    flight_attendant: roster.filter(c => c.role === 'flight_attendant').length,
  };

  // Filter and sort market
  const filteredMarket = roleFilter === 'all'
    ? market
    : market.filter(c => c.role === roleFilter);

  const sortedMarket = [...filteredMarket].sort((a, b) => {
    if (sortBy === 'salary') return a.salary - b.salary;
    if (sortBy === 'experience') return b.experience - a.experience;
    if (sortBy === 'reliability') return b.reliability - a.reliability;
    return 0;
  });

  // Filter roster
  const filteredRoster = roleFilter === 'all'
    ? roster
    : roster.filter(c => c.role === roleFilter);

  return (
    <div style={styles.overlay}>
      <div style={styles.panel}>
        
        <div style={styles.header}>
          <div style={styles.headerTitle}>CREW</div>
          <div style={styles.tabs}>
            <button 
              style={{...styles.tab, ...(viewMode === 'roster' ? styles.tabActive : {})}}
              onClick={() => setViewMode('roster')}
            >
              ROSTER ({roster.length})
            </button>
            <button 
              style={{...styles.tab, ...(viewMode === 'market' ? styles.tabActive : {})}}
              onClick={() => setViewMode('market')}
            >
              MARKET ({market.length})
            </button>
          </div>
          <button style={styles.closeButton} onClick={onClose}>X</button>
        </div>

        {/* Role Filter */}
        <div style={styles.roleFilterBar}>
          <button
            style={{...styles.roleBtn, ...(roleFilter === 'all' ? styles.roleBtnActive : {})}}
            onClick={() => setRoleFilter('all')}
          >
            ALL
          </button>
          <button
            style={{...styles.roleBtn, ...styles.roleCaptain, ...(roleFilter === 'captain' ? styles.roleBtnActive : {})}}
            onClick={() => setRoleFilter('captain')}
          >
            CPT ({viewMode === 'roster' ? rosterCounts.captain : marketCounts.captain})
          </button>
          <button
            style={{...styles.roleBtn, ...styles.roleFO, ...(roleFilter === 'first_officer' ? styles.roleBtnActive : {})}}
            onClick={() => setRoleFilter('first_officer')}
          >
            F/O ({viewMode === 'roster' ? rosterCounts.first_officer : marketCounts.first_officer})
          </button>
          <button
            style={{...styles.roleBtn, ...styles.roleFA, ...(roleFilter === 'flight_attendant' ? styles.roleBtnActive : {})}}
            onClick={() => setRoleFilter('flight_attendant')}
          >
            F/A ({viewMode === 'roster' ? rosterCounts.flight_attendant : marketCounts.flight_attendant})
          </button>
        </div>

        {/* Roster View */}
        {viewMode === 'roster' && (
          <div style={styles.content}>
            {filteredRoster.length === 0 ? (
              <div style={styles.emptyState}>
                {roster.length === 0
                  ? "No crew on roster. Visit the market to hire crew."
                  : `No ${roleFilter.replace('_', ' ')}s on roster.`}
              </div>
            ) : (
              <>
                <div style={styles.summary}>
                  Monthly payroll: ${formatMoney(crewState?.totalMonthlySalary || 0)}
                  <span style={{marginLeft: '20px'}}>
                    Crew: {rosterCounts.captain} CPT / {rosterCounts.first_officer} F/O / {rosterCounts.flight_attendant} F/A
                  </span>
                </div>
                <div style={styles.grid}>
                  {filteredRoster.map(crew => (
                    <CrewCard
                      key={crew.id}
                      crew={crew}
                      isHired
                      onAction={() => onFire(crew.id)}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Market View */}
        {viewMode === 'market' && (
          <div style={styles.content}>
            <div style={styles.sortBar}>
              <span style={styles.sortLabel}>Sort by:</span>
              {['salary', 'experience', 'reliability'].map(opt => (
                <button
                  key={opt}
                  style={{...styles.sortBtn, ...(sortBy === opt ? styles.sortActive : {})}}
                  onClick={() => setSortBy(opt)}
                >
                  {opt.toUpperCase()}
                </button>
              ))}
            </div>
            {sortedMarket.length === 0 ? (
              <div style={styles.emptyState}>
                No {roleFilter === 'all' ? 'crew' : roleFilter.replace('_', ' ') + 's'} available in market.
              </div>
            ) : (
              <div style={styles.grid}>
                {sortedMarket.map(crew => {
                  const totalCost = crew.salary + crew.signingBonus;
                  const canAfford = cash >= totalCost;
                  return (
                    <CrewCard
                      key={crew.id}
                      crew={crew}
                      canAfford={canAfford}
                      onAction={() => canAfford && onHire(crew.id)}
                    />
                  );
                })}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}

// ============================================
// CREW CARD
// ============================================

function CrewCard({ crew, isHired, canAfford = true, onAction }) {
  const tierColors = {
    rookie: '#888',
    journeyman: '#4488ff',
    veteran: '#aa44ff',
    expert: '#ffaa00',
  };

  const roleLabels = {
    captain: 'Captain',
    first_officer: 'First Officer',
    flight_attendant: 'Flight Attendant',
  };

  const roleColors = {
    captain: '#ffcc00',
    first_officer: '#4488ff',
    flight_attendant: '#ff88cc',
  };

  const isCabinCrew = crew.role === 'flight_attendant';

  return (
    <div style={{...styles.card, ...(canAfford ? {} : styles.cardLocked)}}>
      {/* Header */}
      <div style={styles.cardHeader}>
        <div>
          <div style={styles.crewName}>{crew.name}</div>
          <div style={{...styles.crewRole, color: roleColors[crew.role] || '#666'}}>
            {roleLabels[crew.role] || crew.role}
          </div>
        </div>
        <div style={{...styles.tierBadge, borderColor: tierColors[crew.tier], color: tierColors[crew.tier]}}>
          {crew.tier?.toUpperCase()}
        </div>
      </div>

      {/* Stats */}
      <div style={styles.statsGrid}>
        <StatBar label="EXP" value={crew.experience} />
        <StatBar label="REL" value={crew.reliability} />
        {isCabinCrew ? (
          <StatBar label="SVC" value={crew.serviceRating || 50} />
        ) : (
          <StatBar label="FAT" value={crew.fatigueTolerance} />
        )}
      </div>

      {/* Flight Hours */}
      <div style={styles.flightHours}>
        {crew.totalFlightHours?.toLocaleString()} flight hours
      </div>

      {/* Type Ratings (pilots) or Languages (cabin crew) */}
      {isCabinCrew ? (
        <div style={styles.ratingsSection}>
          <div style={styles.ratingsLabel}>LANGUAGES</div>
          <div style={styles.ratings}>
            {crew.languages?.map(lang => (
              <span key={lang} style={styles.ratingBadge}>{lang}</span>
            ))}
          </div>
        </div>
      ) : (
        <div style={styles.ratingsSection}>
          <div style={styles.ratingsLabel}>TYPE RATINGS</div>
          <div style={styles.ratings}>
            {crew.typeRatings?.map(rating => (
              <span key={rating} style={styles.ratingBadge}>{rating}</span>
            ))}
          </div>
        </div>
      )}

      {/* Traits */}
      {crew.traits?.length > 0 && (
        <div style={styles.traitsSection}>
          {crew.traits.map(trait => (
            <span 
              key={trait} 
              style={{
                ...styles.traitBadge,
                ...(isPositiveTrait(trait) ? styles.traitPositive : styles.traitNegative)
              }}
            >
              {formatTrait(trait)}
            </span>
          ))}
        </div>
      )}

      {/* Cost / Action */}
      <div style={styles.cardFooter}>
        {isHired ? (
          <>
            <div style={styles.salary}>${formatMoney(crew.salary)}/mo</div>
            <button style={styles.fireBtn} onClick={onAction}>Release</button>
          </>
        ) : (
          <>
            <div>
              <div style={styles.salary}>${formatMoney(crew.salary)}/mo</div>
              <div style={styles.bonus}>+ ${formatMoney(crew.signingBonus)} signing</div>
            </div>
            <button 
              style={{...styles.hireBtn, ...(canAfford ? {} : styles.btnLocked)}}
              onClick={onAction}
              disabled={!canAfford}
            >
              {canAfford ? 'Hire' : 'No Funds'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ============================================
// STAT BAR
// ============================================

function StatBar({ label, value }) {
  const getColor = (v) => {
    if (v >= 80) return '#00ff88';
    if (v >= 60) return '#88ff00';
    if (v >= 40) return '#ffcc00';
    return '#ff6600';
  };

  return (
    <div style={styles.statBar}>
      <div style={styles.statLabel}>{label}</div>
      <div style={styles.statTrack}>
        <div 
          style={{
            ...styles.statFill,
            width: `${value}%`,
            backgroundColor: getColor(value),
          }}
        />
      </div>
      <div style={styles.statValue}>{value}</div>
    </div>
  );
}

// ============================================
// HELPERS
// ============================================

function formatMoney(cents) {
  if (!cents) return '0';
  if (cents >= 100000000) return `${(cents / 100000000).toFixed(1)}M`;
  if (cents >= 100000) return `${(cents / 100000).toFixed(0)}K`;
  return (cents / 100).toLocaleString();
}

function formatTrait(trait) {
  return trait.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function isPositiveTrait(trait) {
  const positive = ['calm_under_pressure', 'mentor', 'quick_learner', 'fuel_efficient', 
                    'passenger_favorite', 'early_arriver', 'team_player', 'problem_solver'];
  return positive.includes(trait);
}

// ============================================
// STYLES
// ============================================

const styles = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.9)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000,
  },
  panel: {
    width: '900px', maxHeight: '90vh', overflow: 'hidden',
    backgroundColor: '#0a0a0a', border: '1px solid #222', borderRadius: '8px',
    fontFamily: 'inherit',
    display: 'flex', flexDirection: 'column',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '16px 20px', borderBottom: '1px solid #222',
  },
  headerTitle: {
    fontSize: '13px', fontWeight: '600', letterSpacing: '2px', color: '#fff',
  },
  tabs: { display: 'flex', gap: '4px' },
  tab: {
    background: 'none', border: '1px solid #333', borderRadius: '3px',
    padding: '6px 12px', fontSize: '10px', color: '#666', cursor: 'pointer',
    fontFamily: 'inherit',
  },
  tabActive: { borderColor: '#00ff88', color: '#00ff88' },
  closeButton: {
    background: 'none', border: 'none', color: '#666', fontSize: '24px', cursor: 'pointer',
  },
  content: { flex: 1, overflow: 'auto', padding: '20px' },
  emptyState: { color: '#444', fontSize: '12px', fontStyle: 'italic', textAlign: 'center', padding: '40px' },
  summary: {
    fontSize: '11px', color: '#888', marginBottom: '16px', padding: '10px',
    backgroundColor: '#111', borderRadius: '4px',
  },
  sortBar: {
    display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px',
  },
  sortLabel: { fontSize: '10px', color: '#666' },
  sortBtn: {
    background: 'none', border: '1px solid #333', borderRadius: '3px',
    padding: '4px 10px', fontSize: '9px', color: '#666', cursor: 'pointer',
    fontFamily: 'inherit',
  },
  sortActive: { borderColor: '#00ff88', color: '#00ff88' },

  // Role filter
  roleFilterBar: {
    display: 'flex', gap: '8px', padding: '12px 20px',
    borderBottom: '1px solid #222', backgroundColor: '#0d0d0d',
  },
  roleBtn: {
    background: 'none', border: '1px solid #333', borderRadius: '3px',
    padding: '6px 12px', fontSize: '10px', color: '#666', cursor: 'pointer',
    fontFamily: 'inherit', transition: 'all 0.15s',
  },
  roleBtnActive: { borderColor: '#00ff88', color: '#00ff88', backgroundColor: '#0a2a0a' },
  roleCaptain: { borderColor: '#cc9900' },
  roleFO: { borderColor: '#3366cc' },
  roleFA: { borderColor: '#cc6699' },

  grid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' },
  
  // Card
  card: {
    backgroundColor: '#111', border: '1px solid #222', borderRadius: '6px',
    padding: '14px', transition: 'border-color 0.15s',
  },
  cardLocked: { opacity: 0.5 },
  cardHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    marginBottom: '12px',
  },
  crewName: { fontSize: '13px', fontWeight: '600', color: '#fff' },
  crewRole: { fontSize: '10px', color: '#666', marginTop: '2px' },
  tierBadge: {
    fontSize: '9px', fontWeight: '600', letterSpacing: '1px',
    padding: '2px 6px', border: '1px solid', borderRadius: '3px',
  },
  
  // Stats
  statsGrid: { marginBottom: '10px' },
  statBar: { display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' },
  statLabel: { fontSize: '9px', color: '#666', width: '28px' },
  statTrack: {
    flex: 1, height: '4px', backgroundColor: '#222', borderRadius: '2px', overflow: 'hidden',
  },
  statFill: { height: '100%', borderRadius: '2px', transition: 'width 0.3s' },
  statValue: { fontSize: '10px', color: '#888', width: '24px', textAlign: 'right' },
  
  flightHours: { fontSize: '10px', color: '#555', marginBottom: '10px' },
  
  // Ratings
  ratingsSection: { marginBottom: '10px' },
  ratingsLabel: { fontSize: '9px', color: '#666', letterSpacing: '1px', marginBottom: '4px' },
  ratings: { display: 'flex', flexWrap: 'wrap', gap: '4px' },
  ratingBadge: {
    fontSize: '9px', color: '#888', backgroundColor: '#1a1a1a',
    padding: '2px 6px', borderRadius: '3px',
  },
  
  // Traits
  traitsSection: { display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '10px' },
  traitBadge: {
    fontSize: '9px', padding: '2px 6px', borderRadius: '3px',
  },
  traitPositive: { backgroundColor: '#0a2a0a', color: '#00ff88' },
  traitNegative: { backgroundColor: '#2a0a0a', color: '#ff6666' },
  
  // Footer
  cardFooter: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
    borderTop: '1px solid #222', paddingTop: '10px', marginTop: '10px',
  },
  salary: { fontSize: '12px', color: '#fff' },
  bonus: { fontSize: '10px', color: '#666' },
  hireBtn: {
    background: 'none', border: '1px solid #00ff88', borderRadius: '3px',
    padding: '6px 14px', fontSize: '10px', color: '#00ff88', cursor: 'pointer',
    fontFamily: 'inherit',
  },
  btnLocked: { borderColor: '#333', color: '#666', cursor: 'not-allowed' },
  fireBtn: {
    background: 'none', border: '1px solid #661111', borderRadius: '3px',
    padding: '4px 10px', fontSize: '9px', color: '#ff4444', cursor: 'pointer',
    fontFamily: 'inherit',
  },
};

export default CrewMarketPanel;
